import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Scale,
  Loader2,
  Package,
  ChevronDown,
  ChevronRight,
  Printer,
  Copy,
  CheckCircle,
  AlertCircle,
  ShoppingCart,
  ArrowRight,
  Pencil,
  ClipboardCheck,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatQuantidade, formatQuantidadeComUnidade, parseNumero, sanitizarEntradaDecimalBr, numeroParaEntradaBr } from '../utils/numberFormat';
import { rotuloUnidade } from '../utils/unidadeMedida';
import { alocarConsumoFEFO, disponivelNaUnidadeFormulacao } from '../utils/consumoFefo';
import { useRegisterActiveModal } from '../contexts/ActiveModalContext';

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Mesma lógica do Tempo Real / wizard de produção */
function formatarDataSomenteDia(val) {
  if (val == null || val === '') return '—';
  try {
    let d;
    if (typeof val === 'object' && val !== null) {
      if (typeof val.toDate === 'function') d = val.toDate();
      else if (typeof val._seconds === 'number') d = new Date(val._seconds * 1000);
      else if (typeof val.seconds === 'number') d = new Date(val.seconds * 1000);
      else return '—';
    } else if (typeof val === 'string') d = new Date(val);
    else return '—';
    if (!d || Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}

/** Mesma fonte da liberação: soma os consumos do carrinho (inclui linhas duplicadas do mesmo lote). */
function consumosJaRegistradosNoCarrinho(carrinho, materiaPrimaId) {
  const lista = Array.isArray(carrinho?.consumos) ? carrinho.consumos : [];
  const porLote = new Map();
  for (const x of lista) {
    if (String(x.materia_prima_id || '') !== String(materiaPrimaId || '')) continue;
    const recId = String(x.recebimento_id || '');
    const qtd = Number(x.quantidade_utilizada ?? x.quantidade_real_utilizada) || 0;
    const prev = porLote.get(recId);
    if (prev) {
      prev.quantidade += qtd;
    } else {
      porLote.set(recId, {
        quantidade: qtd,
        lote_fornecedor: x.lote_fornecedor,
        fornecedor_nome: x.fornecedor_nome,
        recebimento_id: x.recebimento_id || recId
      });
    }
  }
  return [...porLote.values()];
}

function alertasFaixaCarrinho(ordem, carrinho, rascunhoPorMp = {}) {
  const msgs = [];
  for (const mp of formulacaoBateladaDaOrdem(ordem)) {
    const registrados = consumosJaRegistradosNoCarrinho(carrinho, mp.materia_prima_id);
    const rascunho = rascunhoPorMp[mp.materia_prima_id] || [];
    const total =
      registrados.reduce((s, c) => s + (Number(c.quantidade) || 0), 0) +
      rascunho.reduce((s, c) => s + (Number(c.quantidade) || 0), 0);
    const min = Number(mp.quantidade_planejada_min) || 0;
    const max = Number(mp.quantidade_planejada_max) || 0;
    if (total + 1e-9 < min) {
      msgs.push(
        `${mp.materia_prima_nome}: ${formatQuantidade(total)} ${mp.unidade || ''} (mín. ${formatQuantidade(min)})`.trim()
      );
    } else if (total - 1e-9 > max) {
      msgs.push(
        `${mp.materia_prima_nome}: ${formatQuantidade(total)} ${mp.unidade || ''} (máx. ${formatQuantidade(max)})`.trim()
      );
    }
  }
  return msgs;
}

/**
 * Meta de pesagem por batelada (min/max unitário). Cada carrinho corresponde a 1 batelada.
 * Usa formulacao_por_batelada quando presente; senão divide o total planejado pelo nº de bateladas.
 */
function formulacaoBateladaDaOrdem(ordem) {
  if (Array.isArray(ordem?.formulacao_por_batelada) && ordem.formulacao_por_batelada.length > 0) {
    return ordem.formulacao_por_batelada;
  }
  const n = Number(ordem?.numero_bateladas) || 1;
  return (ordem?.formulacao_planejada || []).map((mp) => ({
    materia_prima_id: mp.materia_prima_id,
    materia_prima_nome: mp.materia_prima_nome,
    quantidade_planejada_min: (Number(mp.quantidade_planejada_min) || 0) / n,
    quantidade_planejada_max: (Number(mp.quantidade_planejada_max) || 0) / n,
    unidade: mp.unidade
  }));
}

/** Cartão por ingrediente — confirma pesagem localmente; registro em lote no final. */
function SeparacaoConsumoMPCard({
  mp,
  lotes,
  consumosRegistrados,
  rascunhoItens,
  onIncluirRascunho,
  onLimparRascunho,
  onEditarRascunhoItem,
  onAjustarConsumo,
  salvandoLote,
  ajustando
}) {
  const totalRegistrado = consumosRegistrados.reduce((sum, c) => sum + c.quantidade, 0);
  const totalRascunho = (rascunhoItens || []).reduce((sum, c) => sum + c.quantidade, 0);
  const totalConsumido = totalRegistrado + totalRascunho;
  const minPlanejado = Number(mp.quantidade_planejada_min) || 0;
  const maxPlanejado = Number(mp.quantidade_planejada_max) || 0;
  const faltaMin = Math.max(0, minPlanejado - totalConsumido);
  const tetoMax = Math.max(0, maxPlanejado - totalConsumido);
  const minimoAtingido = totalConsumido + 1e-9 >= minPlanejado;
  const maximoAtingido = totalConsumido + 1e-9 >= maxPlanejado;
  const acimaDoMaximo = totalConsumido - 1e-9 > maxPlanejado;
  const dentroDaFaixa = minimoAtingido && !acimaDoMaximo;
  const temRascunho = (rascunhoItens || []).length > 0;
  const ocupado = !!salvandoLote || !!ajustando;

  const [adicionarMais, setAdicionarMais] = useState(false);
  const [quantidadeStr, setQuantidadeStr] = useState(() => numeroParaEntradaBr(faltaMin > 0 ? faltaMin : ''));
  const [editandoRecebimentoId, setEditandoRecebimentoId] = useState(null);
  const [editQtdStr, setEditQtdStr] = useState('');
  const [editandoRascunhoId, setEditandoRascunhoId] = useState(null);
  const [editRascunhoQtdStr, setEditRascunhoQtdStr] = useState('');
  const prevAjustandoRef = useRef(false);

  useEffect(() => {
    if (!minimoAtingido) {
      setQuantidadeStr(numeroParaEntradaBr(faltaMin > 0 ? faltaMin : ''));
      setAdicionarMais(false);
    } else {
      setQuantidadeStr('');
    }
  }, [mp.materia_prima_id, minimoAtingido, faltaMin]);

  useEffect(() => {
    if (!editandoRecebimentoId) {
      prevAjustandoRef.current = !!ajustando;
      return;
    }
    const c = consumosRegistrados.find(
      (x) => String(x.recebimento_id) === String(editandoRecebimentoId)
    );
    if (!c) {
      setEditandoRecebimentoId(null);
      setEditQtdStr('');
    } else if (prevAjustandoRef.current && !ajustando) {
      const nova = parseNumero(editQtdStr);
      if (Number.isFinite(nova) && Math.abs(nova - c.quantidade) < 1e-6) {
        setEditandoRecebimentoId(null);
        setEditQtdStr('');
      }
    }
    prevAjustandoRef.current = !!ajustando;
  }, [consumosRegistrados, editandoRecebimentoId, ajustando, editQtdStr]);

  const mostrarFormPesagem = !minimoAtingido || (adicionarMais && !maximoAtingido);
  const podeAdicionarMais = minimoAtingido && !maximoAtingido && lotes.length > 0;

  const quantidadeAlvo = parseNumero(quantidadeStr);
  const lotesParaAlocar = useMemo(() => {
    if (!rascunhoItens?.length) return lotes;
    const reservadoForm = new Map();
    rascunhoItens.forEach((c) => {
      const id = String(c.recebimento_id || '');
      reservadoForm.set(id, (reservadoForm.get(id) || 0) + (Number(c.quantidade) || 0));
    });
    return lotes.map((lote) => {
      const res = reservadoForm.get(String(lote.recebimento_id)) || 0;
      if (res <= 0) return lote;
      const dispForm = disponivelNaUnidadeFormulacao(lote, mp.unidade);
      const restoForm = Math.max(0, dispForm - res);
      const ratio = dispForm > 1e-9 ? restoForm / dispForm : 0;
      return {
        ...lote,
        quantidade_disponivel: Math.max(0, (Number(lote.quantidade_disponivel) || 0) * ratio)
      };
    });
  }, [lotes, rascunhoItens, mp.unidade]);

  const { plano, faltante } = useMemo(
    () =>
      alocarConsumoFEFO(
        lotesParaAlocar,
        Number.isFinite(quantidadeAlvo) && quantidadeAlvo > 0 ? quantidadeAlvo : 0,
        mp.unidade
      ),
    [lotesParaAlocar, quantidadeAlvo, mp.unidade]
  );
  const qtdPorRecebimento = useMemo(() => {
    const m = new Map();
    plano.forEach(({ lote, quantidade }) => {
      m.set(lote.recebimento_id, quantidade);
    });
    return m;
  }, [plano]);

  const podeIncluir =
    mostrarFormPesagem &&
    plano.length > 0 &&
    Number.isFinite(quantidadeAlvo) &&
    quantidadeAlvo > 0 &&
    faltante < 1e-6 &&
    (tetoMax <= 0 || quantidadeAlvo <= tetoMax + 1e-6);

  const iniciarEdicao = (c) => {
    if (!c.recebimento_id) return;
    setEditandoRecebimentoId(c.recebimento_id);
    setEditQtdStr(numeroParaEntradaBr(c.quantidade));
  };

  const cancelarEdicao = () => {
    setEditandoRecebimentoId(null);
    setEditQtdStr('');
  };

  const tetoEditPara = (recebimentoId) => {
    const outros =
      consumosRegistrados
        .filter((c) => String(c.recebimento_id) !== String(recebimentoId))
        .reduce((s, c) => s + c.quantidade, 0) + totalRascunho;
    return Math.max(0, maxPlanejado - outros);
  };

  const salvarEdicao = (c) => {
    const nova = parseNumero(editQtdStr);
    if (!Number.isFinite(nova) || nova < 0) return;
    const teto = tetoEditPara(c.recebimento_id);
    if (maxPlanejado > 0 && nova > teto + 1e-6) return;
    onAjustarConsumo?.(c, nova);
  };

  const incluir = () => {
    if (!podeIncluir || ocupado) return;
    onIncluirRascunho?.(plano);
    setAdicionarMais(false);
    setQuantidadeStr('');
  };

  const classeCard = acimaDoMaximo
    ? 'border-red-400 bg-red-50'
    : minimoAtingido
      ? temRascunho && totalRegistrado + 1e-9 < minPlanejado
        ? 'border-amber-400 bg-amber-50/40'
        : 'border-emerald-400 bg-emerald-50/40'
      : 'border-gray-300 bg-gray-50';

  return (
    <div className={`border-2 rounded-lg p-4 ${classeCard}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-medium text-gray-900">{mp.materia_prima_nome}</h4>
          <p className="text-sm text-gray-600 mt-1">
            Planejado:{' '}
            <span className="font-semibold">
              {formatQuantidade(mp.quantidade_planejada_min)} –{' '}
              {formatQuantidadeComUnidade(mp.quantidade_planejada_max, mp.unidade)}
            </span>
          </p>
          <p className="text-sm text-gray-600">
            Neste carrinho:{' '}
            <span className={`font-semibold ${acimaDoMaximo ? 'text-red-700' : 'text-blue-600'}`}>
              {formatQuantidadeComUnidade(totalConsumido, mp.unidade)}
            </span>
            {temRascunho ? (
              <span className="text-amber-800">
                {' '}
                (registrado {formatQuantidade(totalRegistrado)} + pendente {formatQuantidade(totalRascunho)})
              </span>
            ) : null}
          </p>
          {faltaMin > 0 && (
            <p className="text-sm text-yellow-700 font-medium">
              Falta (mín.): {formatQuantidadeComUnidade(faltaMin, mp.unidade)}
            </p>
          )}
          {acimaDoMaximo && (
            <p className="text-sm text-red-700 font-medium mt-1">
              Acima do máximo da batelada ({formatQuantidade(totalConsumido)} / máx.{' '}
              {formatQuantidadeComUnidade(maxPlanejado, mp.unidade)}). Reduza a quantidade para liberar.
            </p>
          )}
          {dentroDaFaixa && (
            <p className="text-sm text-emerald-800 font-medium mt-1">
              {temRascunho && totalRegistrado + 1e-9 < minPlanejado
                ? 'Mínimo atendido no rascunho — confirme o registro no final.'
                : 'Dentro da faixa da batelada'}
              {!maximoAtingido && tetoMax > 0
                ? ` (ainda é possível adicionar até ${formatQuantidadeComUnidade(tetoMax, mp.unidade)}).`
                : '.'}
            </p>
          )}
        </div>
        {acimaDoMaximo ? (
          <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
        ) : minimoAtingido ? (
          <CheckCircle className="h-6 w-6 text-green-600 shrink-0" />
        ) : null}
      </div>

      {minimoAtingido && !mostrarFormPesagem && (
        <div className="space-y-3">
          {podeAdicionarMais ? (
            <button
              type="button"
              className="text-sm text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
              onClick={() => {
                setAdicionarMais(true);
                setQuantidadeStr('');
              }}
              disabled={ocupado}
            >
              Adicionar mais quantidade (até o máximo)
            </button>
          ) : maximoAtingido ? (
            <p className="text-xs text-gray-500">Quantidade máxima da batelada atingida.</p>
          ) : (
            <p className="text-xs text-gray-500">
              Sem saldo adicional em estoque para este ingrediente.
            </p>
          )}
        </div>
      )}

      {mostrarFormPesagem && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantidade a pesar ({rotuloUnidade(mp.unidade)})
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={quantidadeStr}
              onChange={(e) => setQuantidadeStr(sanitizarEntradaDecimalBr(e.target.value))}
              className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 bg-white"
              placeholder="Ex.: 10,5"
              disabled={ocupado}
            />
            <p className="text-xs text-gray-500 mt-1">
              Use vírgula como decimal. O sistema indica quanto retirar de cada lote (FEFO).
              {tetoMax > 0
                ? ` Máx. restante neste carrinho: ${formatQuantidadeComUnidade(tetoMax, mp.unidade)}.`
                : ''}
            </p>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            <span>
              Lotes (estoque)
              {lotes.length > 0 ? ` — ${lotes.length} lote(s)` : ''}:
            </span>
            {lotes.length > 0 ? (
              <span className="block font-normal text-gray-500 mt-1 text-xs">
                Ordenados por validade (FEFO). Destaque = quantidade que você deve pegar deste lote.
              </span>
            ) : null}
          </label>

          {lotes.length === 0 ? (
            <p className="text-sm text-red-600">Nenhum lote disponível em estoque</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {lotes.map((lote) => {
                const qConsumir = qtdPorRecebimento.get(lote.recebimento_id);
                const noPlano = qConsumir != null && qConsumir > 0;
                return (
                  <div
                    key={lote.recebimento_id || lote.lote_fornecedor}
                    className={`border rounded p-3 transition-all ${
                      noPlano ? 'border-emerald-600 bg-emerald-50' : 'border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900">
                          Lote:{' '}
                          <span className="font-mono">
                            {lote.lote_fornecedor != null && String(lote.lote_fornecedor).trim() !== ''
                              ? lote.lote_fornecedor
                              : '—'}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600">Fornecedor: {lote.fornecedor_nome || '—'}</p>
                        <p className="text-xs text-gray-600">
                          Fabricação:{' '}
                          <span className="font-medium text-gray-800">
                            {formatarDataSomenteDia(lote.data_fabricacao)}
                          </span>
                          {' · '}
                          Validade:{' '}
                          <span className="font-medium text-gray-800">
                            {formatarDataSomenteDia(lote.data_validade)}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600">
                          Disponível:{' '}
                          <span className="font-semibold">
                            {formatQuantidadeComUnidade(
                              lote.quantidade_disponivel,
                              rotuloUnidade(lote.unidade || mp.unidade)
                            )}
                          </span>
                          {(() => {
                            const uLote = rotuloUnidade(lote.unidade || mp.unidade);
                            const uForm = rotuloUnidade(mp.unidade);
                            if (uLote.toLowerCase() === uForm.toLowerCase()) return null;
                            const equiv = disponivelNaUnidadeFormulacao(lote, mp.unidade);
                            return (
                              <span className="text-gray-500">
                                {' '}
                                (≈ {formatQuantidadeComUnidade(equiv, uForm)})
                              </span>
                            );
                          })()}
                        </p>
                      </div>
                      {noPlano ? (
                        <div className="shrink-0 text-right">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">
                            Pegar
                          </p>
                          <p className="text-sm font-bold text-emerald-800">
                            {formatQuantidadeComUnidade(qConsumir, mp.unidade)}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {Number.isFinite(quantidadeAlvo) && quantidadeAlvo > 0 && faltante > 1e-6 && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              Estoque insuficiente: faltam {formatQuantidadeComUnidade(faltante, mp.unidade)} além dos
              lotes disponíveis.
            </p>
          )}

          {Number.isFinite(quantidadeAlvo) &&
            quantidadeAlvo > 0 &&
            tetoMax > 0 &&
            quantidadeAlvo > tetoMax + 1e-6 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Quantidade acima do máximo restante deste carrinho (
                {formatQuantidadeComUnidade(tetoMax, mp.unidade)}).
              </p>
            )}

          {plano.length > 0 && (
            <div className="bg-white border border-emerald-200 rounded-md px-3 py-2 text-xs text-gray-700 space-y-1">
              <p className="font-medium text-emerald-900">Plano de consumo (FEFO):</p>
              {plano.map(({ lote, quantidade }, idx) => (
                <p key={lote.recebimento_id || idx}>
                  {formatQuantidadeComUnidade(quantidade, mp.unidade)} do lote{' '}
                  <span className="font-mono">
                    {lote.lote_fornecedor != null && String(lote.lote_fornecedor).trim() !== ''
                      ? lote.lote_fornecedor
                      : '—'}
                  </span>
                </p>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={incluir}
              disabled={ocupado || !podeIncluir}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              Incluir na lista
            </button>
            {adicionarMais && (
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setAdicionarMais(false);
                  setQuantidadeStr('');
                }}
                disabled={ocupado}
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {temRascunho && (
        <div className="mt-4 bg-amber-50 rounded border border-amber-200 p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h5 className="text-sm font-medium text-amber-900">Pendente de registro (rascunho):</h5>
            <button
              type="button"
              className="text-xs text-amber-900 underline underline-offset-2"
              onClick={() => onLimparRascunho?.()}
              disabled={ocupado}
            >
              Remover da lista
            </button>
          </div>
          <div className="space-y-2">
            {rascunhoItens.map((c, idx) => {
              const editandoR = String(editandoRascunhoId) === String(c.recebimento_id);
              const novaR = parseNumero(editRascunhoQtdStr);
              const outrosRasc =
                rascunhoItens
                  .filter((x) => String(x.recebimento_id) !== String(c.recebimento_id))
                  .reduce((s, x) => s + (Number(x.quantidade) || 0), 0) + totalRegistrado;
              const tetoR = Math.max(0, maxPlanejado - outrosRasc);
              const invalidaR =
                !Number.isFinite(novaR) || novaR < 0 || (maxPlanejado > 0 && novaR > tetoR + 1e-6);
              return (
                <div
                  key={c.recebimento_id || idx}
                  className="text-xs text-amber-950 border border-amber-100 rounded-md bg-white/70"
                >
                  {!editandoR ? (
                    <div className="flex items-stretch gap-0">
                      <div className="flex-1 min-w-0 px-2.5 py-2">
                        <p>
                          {formatQuantidadeComUnidade(c.quantidade, mp.unidade)} — Lote{' '}
                          {c.lote_fornecedor || '—'} ({c.fornecedor_nome || '—'})
                        </p>
                      </div>
                      {onEditarRascunhoItem ? (
                        <button
                          type="button"
                          title="Editar quantidade pesada"
                          className="shrink-0 w-11 border-l border-amber-200 text-amber-900 hover:bg-amber-100 flex items-center justify-center disabled:opacity-50"
                          onClick={() => {
                            setEditandoRascunhoId(c.recebimento_id);
                            setEditRascunhoQtdStr(numeroParaEntradaBr(c.quantidade));
                          }}
                          disabled={ocupado}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="p-2 space-y-2">
                      <p className="font-medium text-gray-800">
                        Editar rascunho — lote <span className="font-mono">{c.lote_fornecedor || '—'}</span>
                      </p>
                      <div className="flex flex-wrap items-end gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editRascunhoQtdStr}
                          onChange={(e) => setEditRascunhoQtdStr(sanitizarEntradaDecimalBr(e.target.value))}
                          className="w-36 border border-gray-300 rounded-md px-2 py-1.5 bg-white text-sm"
                          disabled={ocupado}
                        />
                        <button
                          type="button"
                          disabled={ocupado || invalidaR}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs disabled:opacity-50"
                          onClick={() => {
                            if (invalidaR) return;
                            onEditarRascunhoItem?.(c.recebimento_id, novaR);
                            setEditandoRascunhoId(null);
                            setEditRascunhoQtdStr('');
                          }}
                        >
                          Salvar
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 border rounded-md text-xs"
                          disabled={ocupado}
                          onClick={() => {
                            setEditandoRascunhoId(null);
                            setEditRascunhoQtdStr('');
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        Máx.: {formatQuantidadeComUnidade(tetoR, mp.unidade)}. Use 0 para remover este lote do
                        rascunho.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {consumosRegistrados.length > 0 && (
        <div className="mt-4 bg-white rounded border p-3">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Já registrados neste carrinho:</h5>
          <div className="space-y-2">
            {consumosRegistrados.map((c, idx) => {
              const editando = String(editandoRecebimentoId) === String(c.recebimento_id);
              const tetoEdit = tetoEditPara(c.recebimento_id);
              const novaEdit = parseNumero(editQtdStr);
              const editInvalida =
                !Number.isFinite(novaEdit) ||
                novaEdit < 0 ||
                (maxPlanejado > 0 && novaEdit > tetoEdit + 1e-6);
              return (
                <div
                  key={c.recebimento_id || idx}
                  className="text-xs text-gray-600 border border-gray-200 rounded-md overflow-hidden"
                >
                  {!editando ? (
                    <div className="flex items-stretch gap-0">
                      <div className="flex-1 min-w-0 flex items-center gap-2 px-2.5 py-2">
                        <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                        <span>
                          {formatQuantidadeComUnidade(c.quantidade, mp.unidade)} — Lote {c.lote_fornecedor} (
                          {c.fornecedor_nome || '—'})
                        </span>
                      </div>
                      {c.recebimento_id && onAjustarConsumo ? (
                        <button
                          type="button"
                          title="Editar quantidade registrada"
                          className="shrink-0 w-11 border-l border-gray-200 text-emerald-800 hover:bg-emerald-50 flex items-center justify-center disabled:opacity-50"
                          onClick={() => iniciarEdicao(c)}
                          disabled={ocupado}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-2 bg-amber-50/60 p-2">
                      <p className="font-medium text-gray-800">
                        Ajustar lote <span className="font-mono">{c.lote_fornecedor || '—'}</span>
                      </p>
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="block text-[11px] text-gray-600 mb-0.5">
                            Nova quantidade ({rotuloUnidade(mp.unidade)})
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editQtdStr}
                            onChange={(e) => setEditQtdStr(sanitizarEntradaDecimalBr(e.target.value))}
                            className="w-36 border border-gray-300 rounded-md px-2 py-1.5 bg-white text-sm"
                            disabled={ocupado}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => salvarEdicao(c)}
                          disabled={ocupado || editInvalida}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {ajustando ? 'Salvando…' : 'Salvar ajuste'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelarEdicao}
                          disabled={ocupado}
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-white"
                        >
                          Cancelar
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        Máx. permitido: {formatQuantidadeComUnidade(tetoEdit, mp.unidade)}. Use 0 para
                        remover e devolver ao estoque.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Etapa de conferência — rascunho pendente + o que já está no carrinho. */
function ConferenciaPesagemPanel({
  ordem,
  carrinho,
  rascunhoPorMp,
  onEditarItem,
  onRemoverItem,
  onAjustarRegistrado,
  ocupado
}) {
  const fps = formulacaoBateladaDaOrdem(ordem);
  const [editKey, setEditKey] = useState(null); // `${tipo}::${mpId}::${recebimentoId}`
  const [editStr, setEditStr] = useState('');

  const linhas = useMemo(() => {
    const out = [];
    for (const mp of fps) {
      const registrados = consumosJaRegistradosNoCarrinho(carrinho, mp.materia_prima_id);
      const rascunho = rascunhoPorMp[mp.materia_prima_id] || [];
      const total =
        registrados.reduce((s, c) => s + (Number(c.quantidade) || 0), 0) +
        rascunho.reduce((s, c) => s + (Number(c.quantidade) || 0), 0);
      const min = Number(mp.quantidade_planejada_min) || 0;
      const max = Number(mp.quantidade_planejada_max) || 0;
      const okMin = total + 1e-9 >= min;
      const okMax = total - 1e-9 <= max;
      for (const c of registrados) {
        out.push({ tipo: 'registrado', mp, item: c, totalMp: total, okMin, okMax, min, max });
      }
      for (const c of rascunho) {
        out.push({ tipo: 'rascunho', mp, item: c, totalMp: total, okMin, okMax, min, max });
      }
    }
    return out;
  }, [fps, rascunhoPorMp, carrinho]);

  const alertas = useMemo(() => {
    const msgs = [];
    for (const mp of fps) {
      const registrados = consumosJaRegistradosNoCarrinho(carrinho, mp.materia_prima_id);
      const rascunho = rascunhoPorMp[mp.materia_prima_id] || [];
      if (registrados.length === 0 && rascunho.length === 0) continue;
      const total =
        registrados.reduce((s, c) => s + (Number(c.quantidade) || 0), 0) +
        rascunho.reduce((s, c) => s + (Number(c.quantidade) || 0), 0);
      const min = Number(mp.quantidade_planejada_min) || 0;
      const max = Number(mp.quantidade_planejada_max) || 0;
      if (total + 1e-9 < min) {
        msgs.push(
          `${mp.materia_prima_nome}: abaixo do mínimo (${formatQuantidade(total)} / mín. ${formatQuantidade(min)})`
        );
      }
      if (total - 1e-9 > max) {
        msgs.push(
          `${mp.materia_prima_nome}: acima do máximo (${formatQuantidade(total)} / máx. ${formatQuantidade(max)})`
        );
      }
    }
    return msgs;
  }, [fps, rascunhoPorMp, carrinho]);

  if (linhas.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-8 text-center">
        Nenhum item na lista. Volte à pesagem e use “Incluir na lista”.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-950">
        <p className="font-semibold flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" />
          Conferência final das pesagens
        </p>
        <p className="text-xs mt-1 text-emerald-900/80">
          Revise o que já está no carrinho e o que ainda é rascunho. Use o lápis para corrigir quantidades
          acima do máximo antes de liberar.
        </p>
      </div>

      {alertas.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 space-y-1">
          <p className="font-semibold">Atenção antes de registrar:</p>
          {alertas.map((a) => (
            <p key={a}>• {a}</p>
          ))}
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Ingrediente</th>
              <th className="text-left px-3 py-2 font-medium">Lote</th>
              <th className="text-right px-3 py-2 font-medium">Quantidade</th>
              <th className="text-left px-3 py-2 font-medium">Faixa batelada</th>
              <th className="w-12 px-0" />
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ tipo, mp, item, totalMp, okMin, okMax, min, max }) => {
              const key = `${tipo}::${mp.materia_prima_id}::${item.recebimento_id}`;
              const editando = editKey === key;
              const nova = parseNumero(editStr);
              const teto = Math.max(0, max - (totalMp - (Number(item.quantidade) || 0)));
              const invalida =
                !Number.isFinite(nova) || nova < 0 || (max > 0 && nova > teto + 1e-6);
              return (
                <tr key={key} className="border-t align-top">
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900">{mp.materia_prima_nome}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400">
                      {tipo === 'registrado' ? 'Já no carrinho' : 'Rascunho'}
                    </p>
                    <p
                      className={`text-[11px] ${
                        okMin && okMax ? 'text-emerald-700' : 'text-amber-800'
                      }`}
                    >
                      Total MP: {formatQuantidadeComUnidade(totalMp, mp.unidade)}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{item.lote_fornecedor || '—'}</span>
                    <p className="text-[11px] text-gray-500">{item.fornecedor_nome || '—'}</p>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editando ? (
                      <div className="inline-flex flex-col items-end gap-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editStr}
                          onChange={(e) => setEditStr(sanitizarEntradaDecimalBr(e.target.value))}
                          className="w-28 border rounded-md px-2 py-1 text-sm text-right"
                          disabled={ocupado}
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            disabled={ocupado || invalida}
                            className="text-[11px] px-2 py-0.5 bg-emerald-600 text-white rounded disabled:opacity-50"
                            onClick={() => {
                              if (tipo === 'registrado') {
                                onAjustarRegistrado?.(mp, item, nova);
                              } else {
                                onEditarItem(mp.materia_prima_id, item.recebimento_id, nova);
                              }
                              setEditKey(null);
                              setEditStr('');
                            }}
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            className="text-[11px] px-2 py-0.5 border rounded"
                            disabled={ocupado}
                            onClick={() => {
                              setEditKey(null);
                              setEditStr('');
                            }}
                          >
                            X
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="font-semibold text-gray-900">
                        {formatQuantidadeComUnidade(item.quantidade, mp.unidade)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {formatQuantidade(min)} – {formatQuantidadeComUnidade(max, mp.unidade)}
                  </td>
                  <td className="p-0 border-l align-middle">
                    {!editando ? (
                      <div className="flex flex-col h-full">
                        <button
                          type="button"
                          title="Editar"
                          disabled={ocupado}
                          className="flex-1 min-h-[40px] w-11 flex items-center justify-center text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                          onClick={() => {
                            setEditKey(key);
                            setEditStr(numeroParaEntradaBr(item.quantidade));
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Remover"
                          disabled={ocupado}
                          className="flex-1 min-h-[28px] w-11 text-[10px] border-t text-red-700 hover:bg-red-50 disabled:opacity-50"
                          onClick={() => {
                            if (tipo === 'registrado') {
                              onAjustarRegistrado?.(mp, item, 0);
                            } else {
                              onRemoverItem(mp.materia_prima_id, item.recebimento_id);
                            }
                          }}
                        >
                          Rem.
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SeparacaoPesagemPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [dataRef, setDataRef] = useState(() => toYmd(new Date()));
  const [ordens, setOrdens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [expandida, setExpandida] = useState(null);
  const [carrinhosPorOrdem, setCarrinhosPorOrdem] = useState({});
  const [loadingCarrinhos, setLoadingCarrinhos] = useState({});
  const [criando, setCriando] = useState(null);
  const [liberando, setLiberando] = useState({});
  const [zplModal, setZplModal] = useState({ aberto: false, zpl: '', lote: '' });
  const [ipImpressora, setIpImpressora] = useState(
    () => localStorage.getItem('regulamais_zebra_ip') || ''
  );

  /** Modal: consumo por carrinho — lista todos os ingredientes como no wizard de pesagem */
  const [consumoModal, setConsumoModal] = useState(null);
  const [lotesPorMpId, setLotesPorMpId] = useState({});
  const [lotesModalLoading, setLotesModalLoading] = useState(false);
  /** Rascunho local: { [mpId]: [{ quantidade, lote_fornecedor, fornecedor_nome, recebimento_id }] } */
  const [rascunhoPorMp, setRascunhoPorMp] = useState({});
  const [consumoEtapa, setConsumoEtapa] = useState('pesagem'); // 'pesagem' | 'conferencia'
  const [registrandoLote, setRegistrandoLote] = useState(false);
  const [ajustandoMpId, setAjustandoMpId] = useState(null);
  /** Trava síncrona contra clique duplo antes do setState re-renderizar */
  const registroEmAndamentoRef = useRef(false);

  useRegisterActiveModal(!!consumoModal, {
    id: 'separacao-consumo-carrinho',
    title: consumoModal
      ? `Consumir Ingredientes — ${
          consumoModal.carrinho?.batelada_numero
            ? `Batelada ${consumoModal.carrinho.batelada_numero}${
                consumoModal.carrinho.total_bateladas
                  ? `/${consumoModal.carrinho.total_bateladas}`
                  : ''
              } `
            : ''
        }${consumoModal.carrinho?.lote_carrinho || ''}`.trim()
      : ''
  });

  useRegisterActiveModal(!!zplModal.aberto, {
    id: 'separacao-etiqueta-zpl',
    title: zplModal.aberto ? `Etiqueta — lote ${zplModal.lote || ''}`.trim() : ''
  });

  const loadOrdens = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const listOrdensProducao = httpsCallable(functions, 'listOrdensProducao');
      const r = await listOrdensProducao({
        empresa_id: getEmpresaId(),
        limit: 200
      });
      if (r.data?.success) {
        setOrdens(r.data.data?.items || []);
      } else {
        setOrdens([]);
      }
    } catch (e) {
      setErr(e.message || 'Erro ao carregar ordens');
      setOrdens([]);
    } finally {
      setLoading(false);
    }
  }, [getEmpresaId]);

  useEffect(() => {
    loadOrdens();
  }, [loadOrdens]);

  const isOrdemAbertaPesagem = (o) =>
    o?.fase_operacional === 'aguardando_pesagem' &&
    !['cancelada', 'perda', 'concluida'].includes(String(o?.status || ''));

  /** Ordens do dia selecionado + ordens de dias anteriores ainda abertas (aguardando pesagem). */
  const ordensVisiveis = useMemo(() => {
    const abertas = (ordens || []).filter(isOrdemAbertaPesagem);
    const doDia = abertas.filter((o) => o.data_planejada === dataRef);
    const anteriores = abertas.filter((o) => {
      const d = String(o.data_planejada || '');
      return d && d < dataRef;
    });
    anteriores.sort((a, b) =>
      String(a.data_planejada || '').localeCompare(String(b.data_planejada || ''))
    );
    return [...anteriores, ...doDia];
  }, [ordens, dataRef]);

  const qtdAnterioresAbertas = useMemo(
    () =>
      ordensVisiveis.filter(
        (o) => String(o.data_planejada || '') && String(o.data_planejada) < dataRef
      ).length,
    [ordensVisiveis, dataRef]
  );

  const carregarCarrinhos = async (ordemId, { silent = false } = {}) => {
    if (!silent) setLoadingCarrinhos((p) => ({ ...p, [ordemId]: true }));
    try {
      const fn = httpsCallable(functions, 'listCarrinhosSeparacaoOrdem');
      const r = await fn({ empresa_id: getEmpresaId(), ordem_producao_id: ordemId });
      if (r.data?.success) {
        setCarrinhosPorOrdem((p) => ({
          ...p,
          [ordemId]: r.data.data?.items || []
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoadingCarrinhos((p) => ({ ...p, [ordemId]: false }));
    }
  };

  const toggleExpand = (id) => {
    if (expandida === id) {
      setExpandida(null);
    } else {
      setExpandida(id);
      carregarCarrinhos(id);
    }
  };

  const criarCarrinho = async (ordemId) => {
    setCriando(ordemId);
    setErr('');
    try {
      const fn = httpsCallable(functions, 'criarCarrinhoSeparacao');
      const r = await fn({ empresa_id: getEmpresaId(), ordem_producao_id: ordemId });
      if (r.data?.success) {
        const { zpl, lote_carrinho } = r.data.data || {};
        setZplModal({ aberto: true, zpl: zpl || '', lote: lote_carrinho || '' });
        setMsg(`Carrinho criado — lote ${lote_carrinho}`);
        await carregarCarrinhos(ordemId);
      }
    } catch (e) {
      setErr(e.message || 'Erro ao criar carrinho');
    } finally {
      setCriando(null);
    }
  };

  const imprimirEtiqueta = (carrinho) => {
    setZplModal({
      aberto: true,
      zpl: carrinho.zpl || '',
      lote: carrinho.lote_carrinho || ''
    });
  };

  const liberarCarrinho = async (ordemId, carrinhoId) => {
    setErr('');
    setLiberando((p) => ({ ...p, [carrinhoId]: true }));
    try {
      const fn = httpsCallable(functions, 'liberarCarrinhoSeparacao');
      const r = await fn({ empresa_id: getEmpresaId(), carrinho_id: carrinhoId });
      const setor = r.data?.data?.setor_inicial || '';
      setCarrinhosPorOrdem((p) => ({
        ...p,
        [ordemId]: (p[ordemId] || []).map((c) =>
          c.id === carrinhoId
            ? {
                ...c,
                status: 'liberado',
                producao_carrinho_id: r.data?.data?.carrinho_id || c.producao_carrinho_id
              }
            : c
        )
      }));
      if (r.data?.data?.pesagem_concluida) {
        setOrdens((prev) =>
          (prev || []).map((o) =>
            o.id === ordemId ? { ...o, fase_operacional: 'pesagem_concluida' } : o
          )
        );
      }
      setMsg(`Carrinho liberado para produção${setor ? ` (setor ${setor})` : ''}.`);
      carregarCarrinhos(ordemId, { silent: true });
    } catch (e) {
      setErr(e.message || 'Erro ao liberar carrinho para produção');
    } finally {
      setLiberando((p) => ({ ...p, [carrinhoId]: false }));
    }
  };

  const copiarZpl = async () => {
    try {
      await navigator.clipboard.writeText(zplModal.zpl);
      setMsg('ZPL copiado. Cole no software da Zebra ou envie para a impressora (rede).');
    } catch {
      setErr('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  const salvarIp = () => {
    localStorage.setItem('regulamais_zebra_ip', ipImpressora.trim());
    setMsg('IP salvo neste navegador (lembre-se: o envio direto à rede costuma exigir app/serviço fora do browser).');
  };

  const carregarLotesTodosIngredientes = useCallback(
    async (ordem, { silent = false } = {}) => {
      const fp = ordem.formulacao_planejada || [];
      if (fp.length === 0) {
        setLotesPorMpId({});
        return;
      }
      if (!silent) {
        setLotesModalLoading(true);
        setErr('');
      }
      try {
        const getLotesDisponiveis = httpsCallable(functions, 'getLotesDisponiveis');
        const entries = await Promise.all(
          fp.map(async (mp) => {
            try {
              const result = await getLotesDisponiveis({
                empresa_id: getEmpresaId(),
                materia_prima_id: mp.materia_prima_id
              });
              const lotes = result.data?.success ? result.data.data?.lotes || [] : [];
              return [mp.materia_prima_id, lotes];
            } catch (e) {
              console.warn('Lotes indisponíveis para', mp.materia_prima_nome, e);
              return [mp.materia_prima_id, []];
            }
          })
        );
        setLotesPorMpId(Object.fromEntries(entries));
      } catch (e) {
        console.error(e);
        if (!silent) {
          setErr(e.message || 'Erro ao carregar lotes disponíveis');
          setLotesPorMpId({});
        }
      } finally {
        if (!silent) setLotesModalLoading(false);
      }
    },
    [getEmpresaId]
  );

  const carregarLotesUmIngrediente = useCallback(
    async (materiaPrimaId) => {
      if (!materiaPrimaId) return;
      try {
        const getLotesDisponiveis = httpsCallable(functions, 'getLotesDisponiveis');
        const result = await getLotesDisponiveis({
          empresa_id: getEmpresaId(),
          materia_prima_id: materiaPrimaId
        });
        const lotes = result.data?.success ? result.data.data?.lotes || [] : [];
        setLotesPorMpId((prev) => ({ ...prev, [materiaPrimaId]: lotes }));
      } catch (e) {
        console.warn('Falha ao atualizar lotes do ingrediente', materiaPrimaId, e);
      }
    },
    [getEmpresaId]
  );

  const atualizarOrdemNoModal = async (ordemId) => {
    try {
      const getOrdemProducao = httpsCallable(functions, 'getOrdemProducao');
      const r = await getOrdemProducao({ ordem_producao_id: ordemId });
      if (r.data?.success && r.data.data) {
        const dados = r.data.data;
        setConsumoModal((prev) => {
          if (!prev || prev.ordem.id !== ordemId) return prev;
          return { ...prev, ordem: { id: ordemId, ...dados } };
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const abrirConsumoCarrinho = (ordem, carrinho) => {
    setErr('');
    setMsg('');
    setLotesPorMpId({});
    setRascunhoPorMp({});
    setConsumoEtapa('pesagem');
    setConsumoModal({ ordem, carrinho });
    carregarLotesTodosIngredientes(ordem);
  };

  const fecharConsumoModal = () => {
    if (registrandoLote || registroEmAndamentoRef.current) return;
    setConsumoModal(null);
    setLotesPorMpId({});
    setRascunhoPorMp({});
    setConsumoEtapa('pesagem');
    setRegistrandoLote(false);
    setAjustandoMpId(null);
    registroEmAndamentoRef.current = false;
  };

  const incluirRascunhoMp = (mp, plano) => {
    if (!Array.isArray(plano) || plano.length === 0) return;
    const itens = plano.map(({ lote, quantidade }) => ({
      quantidade: Number(quantidade) || 0,
      lote_fornecedor: lote.lote_fornecedor,
      fornecedor_nome: lote.fornecedor_nome,
      recebimento_id: lote.recebimento_id
    }));
    setRascunhoPorMp((prev) => {
      const atuais = prev[mp.materia_prima_id] || [];
      // Acumula por recebimento (adicionar mais)
      const mapa = new Map();
      for (const c of [...atuais, ...itens]) {
        const id = String(c.recebimento_id || '');
        const prevItem = mapa.get(id);
        if (prevItem) {
          prevItem.quantidade += Number(c.quantidade) || 0;
        } else {
          mapa.set(id, { ...c, quantidade: Number(c.quantidade) || 0 });
        }
      }
      return { ...prev, [mp.materia_prima_id]: [...mapa.values()] };
    });
    setMsg(`Pesagem de “${mp.materia_prima_nome}” incluída na lista. Continue os demais e registre no final.`);
  };

  const limparRascunhoMp = (mpId) => {
    setRascunhoPorMp((prev) => {
      const next = { ...prev };
      delete next[mpId];
      return next;
    });
  };

  const editarRascunhoItem = (mpId, recebimentoId, novaQtd) => {
    const q = Number(novaQtd);
    setRascunhoPorMp((prev) => {
      const atuais = [...(prev[mpId] || [])];
      if (!Number.isFinite(q) || q <= 1e-9) {
        const filtrados = atuais.filter((c) => String(c.recebimento_id) !== String(recebimentoId));
        if (filtrados.length === 0) {
          const next = { ...prev };
          delete next[mpId];
          return next;
        }
        return { ...prev, [mpId]: filtrados };
      }
      return {
        ...prev,
        [mpId]: atuais.map((c) =>
          String(c.recebimento_id) === String(recebimentoId) ? { ...c, quantidade: q } : c
        )
      };
    });
  };

  const removerRascunhoItem = (mpId, recebimentoId) => {
    editarRascunhoItem(mpId, recebimentoId, 0);
  };

  const qtdItensRascunho = useMemo(
    () => Object.values(rascunhoPorMp).reduce((s, arr) => s + (arr?.length || 0), 0),
    [rascunhoPorMp]
  );

  const registrarTodosRascunhos = async () => {
    if (!consumoModal || qtdItensRascunho === 0) return;
    if (registroEmAndamentoRef.current) return;
    registroEmAndamentoRef.current = true;
    const ordemId = consumoModal.ordem.id;
    const carrinhoId = consumoModal.carrinho.id;
    setRegistrandoLote(true);
    setErr('');
    try {
      const itens = [];
      const fps = formulacaoBateladaDaOrdem(consumoModal.ordem);
      for (const [mpId, lista] of Object.entries(rascunhoPorMp)) {
        const mp = fps.find((m) => String(m.materia_prima_id) === String(mpId));
        for (const c of lista || []) {
          if (!c.recebimento_id || !(Number(c.quantidade) > 0)) continue;
          itens.push({
            materia_prima_id: mpId,
            recebimento_id: c.recebimento_id,
            quantidade_utilizada: Number(c.quantidade),
            unidade: mp?.unidade
          });
        }
      }
      if (itens.length === 0) {
        throw new Error('Nenhum item válido no rascunho para registrar.');
      }
      const fn = httpsCallable(functions, 'registrarConsumosCarrinhoLote');
      const r = await fn({
        empresa_id: getEmpresaId(),
        ordem_producao_id: ordemId,
        carrinho_id: carrinhoId,
        itens
      });
      if (!r.data?.success) {
        throw new Error('Não foi possível registrar os consumos em lote.');
      }
      setRascunhoPorMp({});
      setConsumoEtapa('pesagem');
      setMsg(
        `${r.data?.data?.itens_registrados || itens.length} consumo(s) registrados de uma vez no carrinho.`
      );
      await Promise.all([
        atualizarOrdemNoModal(ordemId),
        carregarLotesTodosIngredientes(consumoModal.ordem, { silent: true }),
        carregarCarrinhos(ordemId),
        loadOrdens()
      ]);
    } catch (e) {
      setErr(e.message || e.details || 'Erro ao registrar consumos em lote');
    } finally {
      setRegistrandoLote(false);
      registroEmAndamentoRef.current = false;
    }
  };

  const confirmarConferencia = async () => {
    if (!consumoModal || !carrinhoModal) return;
    if (qtdItensRascunho > 0) {
      await registrarTodosRascunhos();
      return;
    }
    const alertas = alertasFaixaCarrinho(consumoModal.ordem, carrinhoModal);
    if (alertas.length) {
      setErr(
        `Consumo do carrinho não confere com a formulação de uma batelada. ${alertas.slice(0, 3).join(' · ')}${
          alertas.length > 3 ? ` (+${alertas.length - 3})` : ''
        }`
      );
      return;
    }
    setErr('');
    setMsg('Conferência confirmada. A batelada está dentro da faixa e pode ser liberada para produção.');
    fecharConsumoModal();
  };

  const ajustarConsumoRegistrado = async (mp, consumo, novaQuantidade) => {
    if (!consumoModal || !consumo?.recebimento_id) return;
    if (registroEmAndamentoRef.current) return;
    registroEmAndamentoRef.current = true;
    const ordemId = consumoModal.ordem.id;
    const carrinhoId = consumoModal.carrinho.id;
    setAjustandoMpId(mp.materia_prima_id);
    setErr('');
    try {
      const ajustarConsumoMateriaPrima = httpsCallable(functions, 'ajustarConsumoMateriaPrima');
      const r = await ajustarConsumoMateriaPrima({
        empresa_id: getEmpresaId(),
        ordem_producao_id: ordemId,
        materia_prima_id: mp.materia_prima_id,
        recebimento_id: consumo.recebimento_id,
        nova_quantidade_utilizada: novaQuantidade,
        carrinho_id: carrinhoId,
        unidade: mp.unidade
      });
      if (!r.data?.success) {
        throw new Error('Não foi possível ajustar o consumo.');
      }
      setMsg(
        Number(novaQuantidade) <= 0
          ? 'Consumo removido e quantidade devolvida ao estoque.'
          : 'Consumo ajustado com sucesso.'
      );
      await Promise.all([
        atualizarOrdemNoModal(ordemId),
        carregarLotesUmIngrediente(mp.materia_prima_id),
        carregarCarrinhos(ordemId),
        loadOrdens()
      ]);
    } catch (e) {
      setErr(e.message || e.details || 'Erro ao ajustar consumo');
    } finally {
      setAjustandoMpId(null);
      registroEmAndamentoRef.current = false;
    }
  };

  const carrinhoModal = useMemo(() => {
    if (!consumoModal?.carrinho) return null;
    const lista = carrinhosPorOrdem[consumoModal.ordem?.id] || [];
    return lista.find((c) => c.id === consumoModal.carrinho.id) || consumoModal.carrinho;
  }, [consumoModal, carrinhosPorOrdem]);

  if (!hasPermission('manage_production')) {
    return (
      <div className="p-6 text-center text-gray-600">Sem permissão para separação e pesagem.</div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Scale className="h-7 w-7 text-emerald-600" />
          Separação e Pesagem
        </h1>
        <p className="text-gray-600 mt-1">
          Ordens do dia selecionado e ordens de dias anteriores ainda <strong>abertas</strong> (aguardando
          pesagem). O sistema cria <strong>1 carrinho por batelada</strong> (numerados). Pese cada carrinho
          (min–max de uma batelada), imprima a etiqueta e libere cada um para o próximo setor.
        </p>
      </div>

      {err && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{err}</div>
      )}
      {msg && (
        <div className="rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 shrink-0" />
          {msg}
        </div>
      )}

      <div className="bg-white rounded-lg border shadow-sm p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Dia do planejamento</label>
          <input
            type="date"
            className="border rounded-lg px-3 py-2 text-sm"
            value={dataRef}
            onChange={(e) => {
              setDataRef(e.target.value);
              setExpandida(null);
            }}
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">IP da Zebra (referência local)</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="border rounded-lg px-3 py-2 text-sm flex-1"
              placeholder="ex.: 192.168.1.50"
              value={ipImpressora}
              onChange={(e) => setIpImpressora(e.target.value)}
            />
            <button
              type="button"
              onClick={salvarIp}
              className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Salvar
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            O navegador não envia TCP à impressora; use o ZPL copiado no Zebra Setup Utilities ou spooler na mesma
            rede.
          </p>
        </div>
        <button
          type="button"
          onClick={loadOrdens}
          className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin" />
          Carregando ordens…
        </div>
      ) : ordensVisiveis.length === 0 ? (
        <div className="bg-white border rounded-lg p-10 text-center text-gray-500">
          <Package className="h-12 w-12 mx-auto text-gray-300 mb-2" />
          Nenhuma ordem <strong>aguardando pesagem</strong> para {dataRef}
          {dataRef ? ' nem de dias anteriores' : ''}. Use o planejamento e gere as ordens, ou ajuste a data.
        </div>
      ) : (
        <div className="space-y-2">
          {qtdAnterioresAbertas > 0 && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {qtdAnterioresAbertas} ordem(ns) de dia(s) anterior(es) ainda aberta(s) — priorize a conclusão.
            </p>
          )}
          {ordensVisiveis.map((o) => {
            const carrinhosOrdem = carrinhosPorOrdem[o.id] || [];
            const liberados = carrinhosOrdem.filter((c) => c.status === 'liberado').length;
            const totalBat = Number(o.numero_bateladas) || carrinhosOrdem.length;
            const ehAnterior = String(o.data_planejada || '') < dataRef;
            const dataPlanejadaFmt = (() => {
              const d = String(o.data_planejada || '');
              if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return d || '—';
              const [y, m, day] = d.split('-');
              return `${day}/${m}/${y}`;
            })();
            return (
            <div
              key={o.id}
              className={`bg-white border rounded-lg overflow-hidden ${
                ehAnterior ? 'border-amber-300' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => toggleExpand(o.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
              >
                {expandida === o.id ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{o.produto_nome}</p>
                  <p className="text-xs text-gray-500">
                    {o.numero_bateladas} batelada(s)
                    {o.lote_fabricacao ? (
                      <>
                        {' '}
                        · lote <span className="font-mono font-medium text-gray-700">{o.lote_fabricacao}</span>
                      </>
                    ) : null}
                    {' · '}
                    planejada em {dataPlanejadaFmt}
                  </p>
                </div>
                {ehAnterior ? (
                  <span className="text-xs bg-orange-100 text-orange-900 px-2 py-1 rounded shrink-0">
                    Em aberto (dia anterior)
                  </span>
                ) : (
                  <span className="text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded shrink-0">
                    Aguardando pesagem
                  </span>
                )}
              </button>

              {expandida === o.id && (
                <div className="border-t px-4 py-3 bg-gray-50 space-y-3">
                  <p
                    className={`text-xs rounded-md px-3 py-2 border ${
                      totalBat > 0 && liberados >= totalBat
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : 'bg-amber-50 border-amber-200 text-amber-950'
                    }`}
                  >
                    1 carrinho por batelada. Pese cada carrinho (min–max de uma batelada) e libere para produção.{' '}
                    <strong>
                      {liberados}/{totalBat} batelada(s) liberada(s).
                    </strong>
                  </p>
                  <div className="flex flex-wrap gap-2 items-start">
                    <button
                      type="button"
                      disabled={criando === o.id}
                      onClick={() => criarCarrinho(o.id)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-800 text-xs hover:bg-emerald-50 disabled:opacity-50"
                    >
                      {criando === o.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Printer className="h-4 w-4" />
                      )}
                      Carrinho extra (batelada avulsa)
                    </button>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Carrinhos desta ordem</p>
                    {loadingCarrinhos[o.id] ? (
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    ) : (carrinhosPorOrdem[o.id] || []).length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum carrinho ainda.</p>
                    ) : (
                      <ul className="space-y-2">
                        {[...(carrinhosPorOrdem[o.id] || [])]
                          .sort((a, b) => (Number(a.batelada_numero) || 0) - (Number(b.batelada_numero) || 0))
                          .map((c) => (
                          <li
                            key={c.id}
                            className="text-sm bg-white border rounded px-3 py-2 flex flex-col gap-2"
                          >
                            <div className="flex justify-between gap-2 items-center">
                              <span className="flex items-center gap-2 min-w-0">
                                {c.batelada_numero ? (
                                  <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded shrink-0">
                                    Batelada {c.batelada_numero}
                                    {c.total_bateladas ? `/${c.total_bateladas}` : ''}
                                  </span>
                                ) : null}
                                <span className="font-mono truncate">{c.lote_carrinho}</span>
                              </span>
                              <div className="flex items-center gap-2 shrink-0">
                                <span
                                  className={`text-xs ${
                                    c.status === 'liberado' ? 'text-emerald-700 font-medium' : 'text-gray-500'
                                  }`}
                                >
                                  {c.status === 'liberado' ? 'liberado p/ produção' : c.status}
                                </span>
                                {c.zpl ? (
                                  <button
                                    type="button"
                                    onClick={() => imprimirEtiqueta(c)}
                                    title="Ver/imprimir etiqueta ZPL"
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  >
                                    <Printer className="h-3.5 w-3.5" />
                                    Etiqueta
                                  </button>
                                ) : null}
                                {c.status === 'liberado' ? null : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => abrirConsumoCarrinho(o, c)}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-emerald-600 text-emerald-800 hover:bg-emerald-50"
                                    >
                                      <ShoppingCart className="h-3.5 w-3.5" />
                                      Consumir Ingredientes
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => liberarCarrinho(o.id, c.id)}
                                      disabled={!!liberando[c.id] || !(c.consumos && c.consumos.length > 0)}
                                      title={
                                        c.consumos && c.consumos.length > 0
                                          ? 'Liberar este carrinho para o fluxo de produção'
                                          : 'Registre o consumo de ingredientes antes de liberar'
                                      }
                                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                      {liberando[c.id] ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <ArrowRight className="h-3.5 w-3.5" />
                                      )}
                                      Liberar p/ produção
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {(c.consumos && c.consumos.length > 0 && (
                              <ul className="text-xs text-gray-600 border-t pt-2 space-y-1 font-sans">
                                {c.consumos.map((ev, i) => (
                                  <li key={i}>
                                    {ev.materia_prima_nome || ev.materia_prima_id}:{' '}
                                    <span className="font-medium">{formatQuantidade(ev.quantidade_utilizada)}</span>
                                    {ev.lote_fornecedor ? (
                                      <span className="text-gray-500"> · lote forn. {ev.lote_fornecedor}</span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            )) ||
                              null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {consumoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b shrink-0">
              <h2 className="font-semibold text-gray-900 text-lg">
                Consumir Ingredientes —{' '}
                {consumoModal.carrinho.batelada_numero ? (
                  <span className="text-emerald-700">
                    Batelada {consumoModal.carrinho.batelada_numero}
                    {consumoModal.carrinho.total_bateladas
                      ? `/${consumoModal.carrinho.total_bateladas}`
                      : ''}{' '}
                  </span>
                ) : null}
                <span className="font-mono text-emerald-700">{consumoModal.carrinho.lote_carrinho}</span>
              </h2>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${
                    consumoEtapa === 'pesagem'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  1. Pesagem
                </span>
                <span className="text-gray-300">→</span>
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${
                    consumoEtapa === 'conferencia'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  2. Conferência
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {consumoEtapa === 'pesagem' ? (
                  <>
                    Pese cada ingrediente e use <strong>Incluir na lista</strong>. Depois avance para a{' '}
                    <strong>conferência final</strong> antes de gravar no estoque.
                  </>
                ) : (
                  <>
                    Confira todos os itens pesados. Corrija com o <strong>lápis na lateral</strong> se
                    necessário e só então confirme o registro.
                  </>
                )}
              </p>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-4 text-sm">
              {err ? (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm">
                  {err}
                </div>
              ) : null}
              {formulacaoBateladaDaOrdem(consumoModal.ordem).length === 0 ? (
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                  Ordem sem formulação planejada no cadastro do produto.
                </p>
              ) : lotesModalLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  Carregando estoque de todos os ingredientes…
                </div>
              ) : consumoEtapa === 'conferencia' ? (
                <ConferenciaPesagemPanel
                  ordem={consumoModal.ordem}
                  carrinho={carrinhoModal}
                  rascunhoPorMp={rascunhoPorMp}
                  ocupado={registrandoLote || !!ajustandoMpId}
                  onEditarItem={editarRascunhoItem}
                  onRemoverItem={removerRascunhoItem}
                  onAjustarRegistrado={(mp, consumo, novaQtd) =>
                    ajustarConsumoRegistrado(mp, consumo, novaQtd)
                  }
                />
              ) : (
                formulacaoBateladaDaOrdem(consumoModal.ordem).map((mp) => (
                  <SeparacaoConsumoMPCard
                    key={mp.materia_prima_id}
                    mp={mp}
                    lotes={lotesPorMpId[mp.materia_prima_id] || []}
                    consumosRegistrados={consumosJaRegistradosNoCarrinho(
                      carrinhoModal,
                      mp.materia_prima_id
                    )}
                    rascunhoItens={rascunhoPorMp[mp.materia_prima_id] || []}
                    salvandoLote={registrandoLote}
                    ajustando={ajustandoMpId === mp.materia_prima_id}
                    onIncluirRascunho={(plano) => incluirRascunhoMp(mp, plano)}
                    onLimparRascunho={() => limparRascunhoMp(mp.materia_prima_id)}
                    onEditarRascunhoItem={(recebimentoId, novaQtd) =>
                      editarRascunhoItem(mp.materia_prima_id, recebimentoId, novaQtd)
                    }
                    onAjustarConsumo={(consumo, novaQtd) =>
                      ajustarConsumoRegistrado(mp, consumo, novaQtd)
                    }
                  />
                ))
              )}
            </div>
            <div className="px-4 py-3 border-t flex flex-wrap items-center justify-between gap-2 shrink-0">
              <p className="text-xs text-gray-500">
                {qtdItensRascunho > 0
                  ? `${qtdItensRascunho} item(ns) na lista aguardando registro.`
                  : (carrinhoModal?.consumos || []).length > 0
                    ? 'Itens já registrados no carrinho. Avance para conferir e ajustar se estiver fora da faixa.'
                    : 'Nenhum item pendente na lista.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                  onClick={fecharConsumoModal}
                  disabled={registrandoLote || !!ajustandoMpId}
                >
                  Fechar
                </button>
                {consumoEtapa === 'conferencia' ? (
                  <>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                      onClick={() => setConsumoEtapa('pesagem')}
                      disabled={registrandoLote || !!ajustandoMpId}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Voltar à pesagem
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                      onClick={confirmarConferencia}
                      disabled={
                        registrandoLote ||
                        !!ajustandoMpId ||
                        (qtdItensRascunho === 0 && !(carrinhoModal?.consumos || []).length)
                      }
                    >
                      {registrandoLote ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Registrando…
                        </>
                      ) : (
                        <>
                          <ClipboardCheck className="h-4 w-4" />
                          {qtdItensRascunho > 0
                            ? `Confirmar e registrar (${qtdItensRascunho})`
                            : 'Confirmar e registrar'}
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                    onClick={() => setConsumoEtapa('conferencia')}
                    disabled={
                      registrandoLote ||
                      !!ajustandoMpId ||
                      (qtdItensRascunho === 0 && !(carrinhoModal?.consumos || []).length)
                    }
                  >
                    Ir para conferência
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {zplModal.aberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b font-semibold text-gray-900">
              Etiqueta — lote {zplModal.lote}
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-xs text-gray-600 mb-2">
                Copie o ZPL e envie para a impressora na rede (IP salvo: {ipImpressora || '—'}).
              </p>
              <textarea
                readOnly
                className="w-full h-48 text-xs font-mono border rounded-lg p-2 bg-gray-50"
                value={zplModal.zpl}
              />
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-lg text-sm"
                onClick={() => setZplModal({ aberto: false, zpl: '', lote: '' })}
              >
                Fechar
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm inline-flex items-center gap-2"
                onClick={copiarZpl}
              >
                <Copy className="h-4 w-4" />
                Copiar ZPL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeparacaoPesagemPage;
